<template>
    <div class="advisor-preview">
        <div class="advisor-preview__title">
            {{ lang('advisor::advisor.preview.title') }}<template v-if="title"> · {{ title }}</template>
        </div>

        <ul v-if="g.shifts?.length" class="advisor-preview__rows">
            <li v-for="(shift, index) in g.shifts" :key="index" class="advisor-preview__row">
                <span class="advisor-preview__row-main">
                    <span>{{ formatDate(shift.date) }}</span>
                    <span v-if="shift.workplace" class="advisor-preview__row-sub">{{ shift.workplace }}</span>
                </span>
                <span class="advisor-preview__row-val">{{ shift.time }}</span>
            </li>
        </ul>

        <div v-if="g.warnings?.length" class="advisor-preview__warnings">
            <div v-for="(warning, index) in g.warnings" :key="index" class="advisor-preview__warning">
                <span
                    class="advisor-preview__pill"
                    :class="{ 'advisor-preview__pill--danger': warning.severity === 'high' }"
                    >!</span
                >
                <span>{{ warning.message }}</span>
            </div>
        </div>

        <div v-if="!done && !readonly" class="advisor-preview__actions">
            <Button size="sm" :loading="committing" :disabled="committing" @click="confirm">
                {{ lang('advisor::advisor.preview.confirm') }}
            </Button>
            <Button size="sm" variant="secondary" :disabled="committing" @click="$emit('cancel')">
                {{ lang('advisor::advisor.preview.cancel') }}
            </Button>
        </div>

        <div v-if="status" class="advisor-preview__status" :class="{ 'advisor-preview__status--error': error }">
            {{ status }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { lang } from '@/lang/composable';
import { useAdvisorDrawer } from '@src/Advisor/Pages/composables/useAdvisorDrawer';
import type { Grounded } from '@src/Advisor/Pages/types';

const props = defineProps<{ g: Grounded; readonly?: boolean }>();

defineEmits<{ cancel: [] }>();

const drawer = useAdvisorDrawer();

const committing = ref(false);
const done = ref(false);
const error = ref(false);
const status = ref('');

const title = computed(() => props.g.summary ?? [props.g.employee, props.g.workplace].filter(Boolean).join(' · '));

const formatDate = (value: string): string => {
    const d = new Date(`${value}T00:00:00`);

    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' });
};

const confirm = (): void => {
    if (!props.g.skill || !props.g.commit || committing.value) {
        return;
    }

    committing.value = true;
    error.value = false;
    status.value = '';

    drawer
        .commit(props.g.skill, props.g.commit)
        .then(() => {
            done.value = true;
            status.value = lang('advisor::advisor.preview.success');
        })
        .catch((err) => {
            error.value = true;
            status.value = err?.response?.data?.error ?? lang('advisor::advisor.preview.error');
        })
        .finally(() => {
            committing.value = false;
        });
};
</script>

<style scoped>
.advisor-preview {
    margin-top: 0.5rem;
    padding: 0.75rem;
    border: 1px solid rgb(0 0 0 / 0.1);
    border-radius: 0.5rem;
}

.advisor-preview__title {
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.advisor-preview__rows {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.5rem;
}

.advisor-preview__row {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
}

.advisor-preview__row-main {
    display: flex;
    flex-direction: column;
}

.advisor-preview__row-sub {
    font-size: 0.75rem;
    opacity: 0.7;
}

.advisor-preview__row-val {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.advisor-preview__warnings {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.5rem;
}

.advisor-preview__warning {
    display: flex;
    gap: 0.5rem;
    align-items: baseline;
    font-size: 0.875rem;
}

.advisor-preview__pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.1rem;
    height: 1.1rem;
    border-radius: 9999px;
    background: #f59e0b;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
}

.advisor-preview__pill--danger {
    background: #dc2626;
}

.advisor-preview__actions {
    display: flex;
    gap: 0.5rem;
}

.advisor-preview__status {
    margin-top: 0.5rem;
    font-size: 0.875rem;
}

.advisor-preview__status--error {
    color: #dc2626;
}
</style>
